import { useState } from 'react';
import { SectionLabel, R, Bar } from '../components/primitives.js';
import { PHASES, ST } from '../data/mock-data.js';
import type { CanopusOrStatus } from '../../../services/canopusCommand.js';
import {
  adaptRoom,
  adaptEvent,
  adaptCase,
  type AdaptedRoom,
  type AdaptedEvent,
  type AdaptedCase,
  type AdaptedPhase,
  type AdaptedStep,
} from './or-adapters.js';
import './or-overview.css';

type StatusKey = keyof typeof ST;

// Partition rooms by current activity for the three-section OR overview layout.
// - ongoing: an active case is in progress (procedure attached, status not 'done')
// - earlier: a case actually finished TODAY in this room (status === 'done')
// - none: no active case and no case finished today (may still have an older
//   lastCase rendered on the card for context)
export function partitionRooms(rooms: AdaptedRoom[]): {
  ongoing: AdaptedRoom[];
  earlier: AdaptedRoom[];
  none: AdaptedRoom[];
} {
  const ongoing: AdaptedRoom[] = [];
  const earlier: AdaptedRoom[] = [];
  const none: AdaptedRoom[] = [];
  for (const r of rooms) {
    if (r.procedure !== null && r.status !== 'done') {
      ongoing.push(r);
    } else if (r.status === 'done') {
      earlier.push(r);
    } else {
      none.push(r);
    }
  }
  return { ongoing, earlier, none };
}

// TFI thresholds → semantic color (>=75 great, >=50 ok, >=25 warn, else bad).
function tfiColor(tfi: number): string {
  if (tfi >= 75) return '#34D399';
  if (tfi >= 50) return '#06B6D4';
  if (tfi >= 25) return '#FBBF24';
  return '#F87171';
}

interface CardProps {
  room: AdaptedRoom;
  onClick: (room: AdaptedRoom) => void;
  sel: boolean;
  loading: boolean;
}

// ─── Card ─ OR room tile (mockup line 2963) ──────────────────────
function Card({ room, onClick, sel, loading }: CardProps) {
  const s = ST[room.status as StatusKey];
  const lb = loading ? 'loading-blur' : undefined;
  const pc =
    room.pace === 'on'
      ? '#0FB7D1'
      : room.pace === 'over'
        ? '#FFB300'
        : room.pace === 'slight'
          ? '#FBBF24'
          : room.pace === 'new'
            ? '#6366F1'
            : '#667788';
  const paceMatch = room.paceText ? room.paceText.match(/(\d+ min)/) : null;
  const paceMatchVal = paceMatch ? paceMatch[1] : 'over';
  const paceLabel =
    room.pace === 'on'
      ? 'On pace'
      : room.pace === 'over'
        ? `+${paceMatchVal}`
        : room.pace === 'slight'
          ? `+${paceMatchVal}`
          : room.pace === 'new'
            ? `${room.caseCount}/15 cases`
            : null;
  return (
    // border color comes from status (s.c when sel, s.bd otherwise) — kept inline.
    <div
      onClick={() => onClick(room)}
      className={`or-card ${sel ? 'or-card--sel' : 'or-card--unsel'}`}
      style={{ border: `2px solid ${sel ? s.c : s.bd}` }}
    >
      <div className="or-card-head">
        <div className="or-card-name-wrap">
          <span className="or-card-name">{room.name}</span>
          {/* status badge color/background driven by ST entry */}
          <span className="or-status-badge" style={{ color: s.c, background: s.bg }}>
            {s.l}
          </span>
        </div>
        {room.elapsed && (
          <span className={`or-card-elapsed ${lb ?? ''}`} style={{ color: s.c }}>
            {room.elapsed}
          </span>
        )}
      </div>
      {room.procedure ? (
        <div className={`or-card-body ${lb ?? ''}`}>
          <div className="or-card-procedure">{room.procedure}</div>
          <div className="or-card-meta-row">
            <div className="or-card-surgeon">{room.surgeon}</div>
            {room.tfi !== undefined && room.tfi !== null && (
              <div className="or-tfi-wrap">
                {/* TFI color is computed from numeric value — kept inline. */}
                <span className="or-tfi-label" style={{ color: tfiColor(room.tfi) }}>
                  TFI: {room.tfi}
                </span>
                <div className="or-tfi-dot" style={{ background: tfiColor(room.tfi) }} />
              </div>
            )}
          </div>
          <div className="or-flex-spacer" />
          {room.status !== 'done' && (
            <>
              <div className="or-phase-bar-row">
                {PHASES.map((_, i) => {
                  const pi = room.pi ?? 0;
                  // each cell color depends on phase position vs current — kept inline.
                  return (
                    <div
                      key={i}
                      className="or-phase-bar-cell"
                      style={{
                        background: i < pi ? '#0FB7D1' : i === pi ? s.c : 'rgba(255,255,255,0.06)',
                        opacity: i <= pi ? 1 : 0.4,
                      }}
                    />
                  );
                })}
              </div>
              {room.currentStepName && (
                <div className="or-step-row">
                  <div className="or-step-pulse" style={{ background: s.c }} />
                  <span className="or-step-name">{room.currentStepName}</span>
                  <span className="or-step-count">
                    {room.currentStep}/{room.totalSteps}
                  </span>
                </div>
              )}
              <div className="or-pace-row">
                <span className="or-pace-label" style={{ color: pc }}>
                  {room.pace === 'on' ? '✓' : room.pace === 'new' ? '○' : '⚠'} {paceLabel}
                </span>
                {room.estRem && room.estRem !== '--' && (
                  <span className="or-pace-rem">~{room.estRem} left</span>
                )}
              </div>
            </>
          )}
          {room.status === 'done' && (
            <div className="or-done-row">
              <span className="or-done-text">Done at {room.doneAt}</span>
              <span className="or-done-idle">Idle {room.idleFor}</span>
            </div>
          )}
          {(room.trackingStale || room.alerts.length > 0) && (
            <div className="or-alerts">
              {room.trackingStale && (
                <div className="or-alert or-alert--warning">{'⚠'} No active tracking</div>
              )}
              {room.alerts.map((alertText, i) => (
                <div key={i} className="or-alert">
                  {'⚠'} {alertText}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className={`or-card-body ${lb ?? ''}`}>
          <div className="or-card-empty-text">No active recording</div>
          {room.lastCase && <div className="or-card-last-case">Last: {room.lastCase}</div>}
        </div>
      )}
    </div>
  );
}

interface DetailProps {
  room: AdaptedRoom;
  onClose: () => void;
  // controls a demo-only hide of the Circ Nurse row on
  // active procedures (the demo doesn't populate a Circ Nurse, so the row
  // is just clutter). Real mode falls back to the "hide when empty" rule.
  demoMode?: boolean;
}

// ─── Detail ─ OR detail drawer (mockup line 3186) ────────────────
function Detail({ room, onClose, demoMode = false }: DetailProps) {
  const [showAllSteps, setShowAllSteps] = useState(false);
  if (!room || !room.procedure) return null;
  const s = ST[room.status as StatusKey];
  const pi = room.pi ?? 0;
  const phases: AdaptedPhase[] = room.phases ?? [];
  const steps: AdaptedStep[] = room.steps || [];
  const currentPhaseSteps = steps.filter((st) => st.phase === pi);
  const completedSteps = steps.filter((st) => st.done).length;

  // Group steps by phase for the expanded view
  const stepsByPhase: Record<number, AdaptedStep[]> = {};
  steps.forEach((st) => {
    if (!stepsByPhase[st.phase]) stepsByPhase[st.phase] = [];
    stepsByPhase[st.phase]!.push(st);
  });

  return (
    <div className="or-detail">
      <div className="or-detail-head">
        <div className="or-detail-title-wrap">
          <span className="or-detail-title">{room.name}</span>
          {/* status pill colors come from ST entry */}
          <span className="or-status-pill" style={{ color: s.c, background: s.bg }}>
            {s.l}
          </span>
          <span className="or-detail-procedure">{room.procedure}</span>
          {room.totalSteps && (
            <span className="or-step-counter">
              Step {room.currentStep} of {room.totalSteps}
            </span>
          )}
        </div>
        <button onClick={onClose} className="or-detail-close">
          Close
        </button>
      </div>
      <div className="or-detail-grid">
        <div>
          <SectionLabel text="Case Details (from Canopus recording)" />
          <R label="Surgeon" value={room.surgeon} />
          {room.scrub && <R label="Scrub Tech" value={room.scrub} />}
          {/* Demo mode hides Circ Nurse for active procedures (no demo data
              for it). Real mode falls back to the standard "hide when empty"
              rule. */}
          {!(demoMode && room.status !== 'done') && room.circ && (
            <R label="Circ Nurse" value={room.circ} />
          )}
          <R label="Surgical Profile" value={room.profile} hl={true} />
          <R label="Device" value={room.device} />
          <R label="Recording Started" value={room.started} />
          <R label="Last Connection" value={room.lastInteraction} />
          <R label="Elapsed" value={room.elapsed} />
          {room.hasAvg ? (
            <>
              <R
                label="Historical Avg"
                value={`${room.avgDur} (${room.caseCount} cases)${
                  room.expectedAdj ? ` • team-adj: ${room.expectedAdj}` : ''
                }`}
              />
              <R label="Est. Remaining" value={room.estRem || '--'} />
            </>
          ) : (
            <R
              label="Historical Avg"
              value={`Building — ${room.caseCount} of 15+ cases needed`}
              warn={true}
            />
          )}
        </div>
        <div>
          <SectionLabel text="Phase Timing vs. Historical Avg" />
          {!room.hasAvg && (
            <div className="or-detail-info-banner">
              Insufficient data for comparison. Showing raw timing only.
            </div>
          )}
          {phases.length > 0 ? (
            phases.map((pt, i) => {
              const act = pt.d === 'In progress' || pt.d === 'Building data';
              const over = pt.d.startsWith('+');
              const c = act ? s.c : over ? '#FBBF24' : '#0FB7D1';
              return (
                <div key={i} className="or-phase-row">
                  {/* dot color: status when active, accent otherwise */}
                  <div className="or-phase-dot" style={{ background: act ? s.c : '#0FB7D1' }} />
                  <span className="or-phase-name">{pt.p}</span>
                  <span className="or-phase-dur">{pt.dur}</span>
                  {room.hasAvg && <span className="or-phase-avg">avg {pt.avg}</span>}
                  {/* delta color depends on act/over flags */}
                  <span className="or-phase-delta" style={{ color: c }}>
                    {pt.d}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="or-no-phase-data">Case completed. Full phase data in Analytics.</div>
          )}
          {room.status !== 'done' &&
            PHASES.slice(phases.length).map((p, i) => (
              <div key={`f${i}`} className="or-phase-row or-phase-row--future">
                <div className="or-phase-dot or-phase-dot--idle" />
                <span className="or-phase-name or-phase-name--future">{p}</span>
                <span className="or-phase-dur or-phase-dur--future">--</span>
              </div>
            ))}
        </div>
        <div>
          <div className="or-steps-head">
            <SectionLabel text="Live Reference Guide Steps" />
            {steps.length > 0 && (
              <button onClick={() => setShowAllSteps(!showAllSteps)} className="or-steps-toggle">
                {showAllSteps ? 'Current Phase' : 'All Steps'}
              </button>
            )}
          </div>
          {steps.length > 0 && (
            <>
              <div className="or-progress-row">
                <Bar
                  pct={room.totalSteps ? (completedSteps / room.totalSteps) * 100 : 0}
                  color={s.c}
                  h={6}
                />
                <span className="or-progress-count">
                  {completedSteps}/{room.totalSteps}
                </span>
              </div>
              {!showAllSteps ? (
                <>
                  {/* current phase tag color/background driven by status */}
                  <div className="or-current-phase-tag" style={{ color: s.c, background: s.bg }}>
                    {PHASES[pi]}
                  </div>
                  {currentPhaseSteps.map((st, i) => (
                    <div
                      key={i}
                      className={`or-step-item ${st.active ? 'or-step-item--active' : ''}`}
                    >
                      {/* circle background/border drive off status colors */}
                      <div
                        className={`or-step-circle ${st.done ? 'or-step-circle--done' : ''}`}
                        style={
                          st.active ? { background: s.bg, border: `2px solid ${s.c}` } : undefined
                        }
                      >
                        {st.done && <span className="or-step-check">{'✓'}</span>}
                        {st.active && (
                          <div className="or-step-active-dot" style={{ background: s.c }} />
                        )}
                      </div>
                      <div className="or-step-text-wrap">
                        <div
                          className={`or-step-text ${
                            st.done
                              ? 'or-step-text--done'
                              : st.active
                                ? 'or-step-text--active'
                                : 'or-step-text--pending'
                          }`}
                        >
                          {st.name}
                        </div>
                        {st.time && <div className="or-step-time">{st.time}</div>}
                        {st.active && (
                          <div className="or-step-progress-text" style={{ color: s.c }}>
                            In progress...
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="or-all-steps-scroll">
                    {Object.keys(stepsByPhase)
                      .map((k) => parseInt(k, 10))
                      .sort((a, b) => a - b)
                      .map((phasePi) => {
                        const phaseSteps = stepsByPhase[phasePi] ?? [];
                        const isCurrentPhase = phasePi === pi;
                        const isPast = phasePi < pi;
                        const isFuture = phasePi > pi;
                        return (
                          <div key={phasePi} className="or-all-phase-block">
                            {/* phase tag color/background depends on past/current */}
                            <div
                              className={`or-all-phase-tag ${
                                isPast ? 'or-all-phase-tag--past' : ''
                              } ${isFuture ? 'or-all-phase-tag--future' : ''}`}
                              style={isCurrentPhase ? { color: s.c, background: s.bg } : undefined}
                            >
                              {PHASES[phasePi]}
                              {isPast && <span className="or-all-past-check">{'✓'}</span>}
                            </div>
                            {phaseSteps.map((st, i) => (
                              <div
                                key={i}
                                className={`or-all-step-item ${
                                  st.active ? 'or-all-step-item--active' : ''
                                } ${isFuture ? 'or-all-step-item--future' : ''}`}
                              >
                                <div
                                  className={`or-all-step-circle ${
                                    st.done ? 'or-all-step-circle--done' : ''
                                  }`}
                                  style={
                                    st.active
                                      ? { background: s.bg, border: `2px solid ${s.c}` }
                                      : undefined
                                  }
                                >
                                  {st.done && <span className="or-all-step-check">{'✓'}</span>}
                                  {st.active && (
                                    <div
                                      className="or-all-step-active-dot"
                                      style={{ background: s.c }}
                                    />
                                  )}
                                </div>
                                <span
                                  className={`or-all-step-text ${
                                    st.done
                                      ? 'or-all-step-text--done'
                                      : st.active
                                        ? 'or-all-step-text--active'
                                        : 'or-all-step-text--pending'
                                  }`}
                                >
                                  {st.name}
                                </span>
                                {st.time && <span className="or-all-step-time">{st.time}</span>}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
            </>
          )}
          {steps.length === 0 && (
            <div className="or-no-step-data">
              {room.status === 'done'
                ? 'Case completed. Step data available in case history.'
                : 'No step data available.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface EventsProps {
  events: AdaptedEvent[] | null;
  loading: boolean;
}

// ─── Events ─ live events feed (mockup line 3648) ────────────────
function Events({ events, loading }: EventsProps) {
  const sc: Record<AdaptedEvent['sev'], string> = {
    high: '#FFB300',
    medium: '#FBBF24',
    low: '#4B5563',
    info: '#0FB7D1',
  };
  const list: AdaptedEvent[] = events || [];
  const lb = loading ? 'loading-blur' : undefined;
  const showSkeleton = loading && list.length === 0;
  return (
    <div className="or-panel">
      <SectionLabel text="Live Events (from reference guide check-offs)" />
      {showSkeleton
        ? Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={`or-event-row ${i < 3 ? 'or-event-row--with-border' : ''}`}>
              <div className="or-event-dot" style={{ background: sc.info }} />
              <div className="loading-blur">
                <div className="or-event-msg">Event message placeholder</div>
                <div className="or-event-time">2 min ago</div>
              </div>
            </div>
          ))
        : list.map((e, i) => (
            <div
              key={i}
              className={`or-event-row ${i < list.length - 1 ? 'or-event-row--with-border' : ''}`}
            >
              {/* dot color is severity-driven */}
              <div className="or-event-dot" style={{ background: sc[e.sev] }} />
              <div className={lb}>
                <div className="or-event-msg">{e.msg}</div>
                <div className="or-event-time">{e.t}</div>
              </div>
            </div>
          ))}
    </div>
  );
}

interface CaseLogProps {
  cases: AdaptedCase[] | null;
  loading: boolean;
}

// ─── CaseLog ─ today's recorded cases (mockup line 3694) ─────────
function CaseLog({ cases: realCases, loading }: CaseLogProps) {
  const cases: AdaptedCase[] = realCases || [];
  return (
    <div className="or-panel">
      <SectionLabel text="Today's Recorded Cases" />
      <div className="or-cases-head">
        {['Room', 'Procedure', 'Surgeon', 'Elapsed', 'Avg (n)', 'vs Avg'].map((h, i) => (
          // header cells use varied flex/text alignment per index — minimal inline style.
          <span
            key={i}
            className="or-cases-head-cell"
            style={{ flex: i === 1 ? 2 : 1, textAlign: i > 2 ? 'right' : 'left' }}
          >
            {h}
          </span>
        ))}
      </div>
      {loading && cases.length === 0
        ? Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="or-case-row">
              <div className="or-case-or-wrap">
                <div className="or-case-dot" style={{ background: '#667788' }} />
                <span className="or-case-or loading-blur">OR 1</span>
              </div>
              <span className="or-case-procedure loading-blur">Procedure Name</span>
              <span className="or-case-surgeon loading-blur">Dr. Surgeon</span>
              <span className="or-case-dur loading-blur">--</span>
              <span className="or-case-avg loading-blur">--</span>
              <span className="or-case-delta loading-blur" style={{ color: '#6366F1' }}>
                --
              </span>
            </div>
          ))
        : cases.map((c, i) => {
            const colorMap: Record<AdaptedCase['st'], string> = {
              done: '#34D399',
              long: '#FBBF24',
              closing: '#818CF8',
              active: '#0FB7D1',
              new: '#6366F1',
            };
            const color = colorMap[c.st] || '#667788';
            const dc = c.d.startsWith('+') ? '#FBBF24' : c.d === 'On pace' ? '#0FB7D1' : '#6366F1';
            return (
              <div
                key={i}
                className={`or-case-row ${c.st === 'done' ? 'or-case-row--done' : ''} ${
                  loading ? 'loading-blur' : ''
                }`}
              >
                <div className="or-case-or-wrap">
                  {/* dot color encodes status */}
                  <div className="or-case-dot" style={{ background: color }} />
                  <span className="or-case-or">{c.o}</span>
                </div>
                <span className="or-case-procedure">{c.p}</span>
                <span className="or-case-surgeon">{c.s}</span>
                <span className="or-case-dur">{c.dur}</span>
                <span className="or-case-avg">
                  {c.avg}
                  {c.n > 0 && c.avg !== '--' ? ` (${c.n})` : c.avg === '--' ? ` (${c.n})` : ''}
                </span>
                {/* delta color encodes pace ('+' = warning) */}
                <span className="or-case-delta" style={{ color: dc }}>
                  {c.d}
                </span>
              </div>
            );
          })}
    </div>
  );
}

// ─── ORTab ─ OR Overview tab (mockup line 8718) ──────────────────
// `orStatus` + `isLoading` are provided by the shell so the page-level
// metric strip and the tab render off the same fetched state.
export function ORTab({
  orStatus,
  isLoading,
  roomLabel: _roomLabel = 'OR',
  // forwarded to Detail so it can apply demo-only display
  // rules (e.g. hiding the Circ Nurse row for active procedures).
  demoMode = false,
}: {
  orStatus: CanopusOrStatus | null;
  isLoading: boolean;
  roomLabel?: string;
  demoMode?: boolean;
}) {
  const [sel, setSel] = useState<AdaptedRoom | null>(null);

  const adaptedRooms = (orStatus ? orStatus.rooms.map(adaptRoom) : []).sort((a, b) => {
    const numA = parseInt(a.name.replace(/\D/g, ''), 10) || 0;
    const numB = parseInt(b.name.replace(/\D/g, ''), 10) || 0;
    return numA - numB;
  });
  const adaptedEvents: AdaptedEvent[] | null = orStatus ? orStatus.events.map(adaptEvent) : null;

  const activeRoomNames = new Set(
    adaptedRooms.filter((r) => r.procedure !== null && r.status !== 'done').map((r) => r.name)
  );
  const filteredEvents: AdaptedEvent[] | null = adaptedEvents
    ? adaptedEvents.filter((e) => {
        // Events have format "RoomName: StepName" in msg field.
        const roomName = e.msg.split(':')[0]?.trim();
        return roomName && activeRoomNames.has(roomName);
      })
    : null;

  const adaptedCases: AdaptedCase[] | null = orStatus ? orStatus.todaysCases.map(adaptCase) : null;

  const initialLoading = isLoading && adaptedRooms.length === 0;
  const groups = partitionRooms(adaptedRooms);

  const renderRoomGrid = (rooms: AdaptedRoom[], emptyText: string) => {
    if (rooms.length === 0) {
      return <div className="or-section-empty">{emptyText}</div>;
    }
    return (
      <div className="or-rooms-grid">
        {rooms.map((r) => (
          <div key={r.id} className="or-room-cell">
            <Card room={r} onClick={setSel} sel={!!sel && sel.id === r.id} loading={isLoading} />
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {initialLoading ? (
        <div className="or-section">
          <div className="or-section-header">
            <SectionLabel text="Ongoing Cases" />
          </div>
          <div className="or-rooms-grid">
            {Array.from({ length: 4 }).map((_, idx) => {
              const s = ST.active;
              return (
                <div key={idx} className="or-room-cell">
                  <div className="or-card or-card--unsel" style={{ border: `2px solid ${s.bd}` }}>
                    <div className="or-card-head">
                      <div className="or-card-name-wrap">
                        <span className="or-card-name loading-blur">OR 1</span>
                        <span
                          className="or-status-badge loading-blur"
                          style={{ color: s.c, background: s.bg }}
                        >
                          Active
                        </span>
                      </div>
                    </div>
                    <div className="or-card-body loading-blur">
                      <div className="or-card-procedure">Procedure Name</div>
                      <div className="or-card-meta-row">
                        <div className="or-card-surgeon">Dr. Surgeon</div>
                      </div>
                      <div className="or-flex-spacer" />
                      <div className="or-phase-bar-row">
                        {PHASES.map((_, i) => (
                          <div
                            key={i}
                            className="or-phase-bar-cell"
                            style={{
                              background: 'rgba(255,255,255,0.06)',
                              opacity: 0.4,
                            }}
                          />
                        ))}
                      </div>
                      <div className="or-pace-row">
                        <span className="or-pace-label" style={{ color: '#0FB7D1' }}>
                          ✓ On pace
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          <div className="or-section">
            <div className="or-section-header">
              <SectionLabel text="Ongoing Cases" />
            </div>
            {renderRoomGrid(groups.ongoing, 'No active cases right now.')}
            {sel && sel.procedure !== null && (
              <Detail room={sel} onClose={() => setSel(null)} demoMode={demoMode} />
            )}
          </div>
          <div className="or-section">
            <div className="or-section-header">
              <SectionLabel text="Earlier Today" />
            </div>
            {renderRoomGrid(groups.earlier, 'No cases finished earlier today.')}
          </div>
          <div className="or-section">
            <div className="or-section-header">
              <SectionLabel text="No Cases Yet" />
            </div>
            {renderRoomGrid(groups.none, 'All rooms have had at least one case today.')}
          </div>
        </>
      )}
      <div className="or-bottom-grid">
        <Events events={filteredEvents} loading={isLoading} />
        <CaseLog cases={adaptedCases} loading={isLoading} />
      </div>
    </>
  );
}
